import React from 'react';
import { 
  FaShoppingCart, 
  FaTimesCircle, 
  FaClock, 
  FaCheckCircle, 
  FaTruck, 
  FaBoxOpen, 
  FaDollarSign, 
  FaShippingFast,
  FaExchangeAlt,
  FaBox,
  FaMoneyBill
} from 'react-icons/fa';

const StatusSummary = ({ statusSummary, onStatusFilterChange = () => {}, activeStatus = '' }) => {
    const containerRef = React.useRef(null);

    // Status mapping object - now we're passing the key directly, not the display value
    const statusApiMapping = {
        allOrders: '', // Empty for all orders
        abandoned: 'abandoned',
        pending: 'pending',
        confirmed: 'confirmed',
        readyToShip: 'readyToShip',
        shipped: 'shipped',
        delivered: 'delivered',
        canceled: 'canceled',
        paid: 'paid',
        return: 'return',     // Add "Retour" status
        returnReceived: 'returnReceived', // Add "Retour reçu" status
        dechet: 'dechet', // Add "Déchet" status
    };

    // Reverse mapping: API value -> statusKey
    const apiToKeyMapping = Object.fromEntries(
        Object.entries(statusApiMapping).map(([key, value]) => [value, key])
    );

    // Determine which key is currently active
    const activeKey = apiToKeyMapping[activeStatus] || 'allOrders';

    const getStatusBadgeStyle = (key) => {
        switch (key) {
            case 'allOrders': return 'bg-indigo-50 border-indigo-200 text-indigo-800';
            case 'abandoned': return 'bg-gray-100 border-gray-200 text-gray-800';
            case 'pending': return 'bg-amber-50 border-amber-200 text-amber-800';
            case 'confirmed': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
            case 'readyToShip': return 'bg-emerald-50 border-emerald-200 text-emerald-800';
            case 'shipped': return 'bg-blue-50 border-blue-200 text-blue-800';
            case 'delivered': return 'bg-green-50 border-green-200 text-green-800';
            case 'canceled': return 'bg-red-50 border-red-200 text-red-800';
            case 'paid': return 'bg-indigo-50 border-indigo-200 text-indigo-800';
            case 'return': return 'bg-orange-50 border-orange-200 text-orange-800'; // Style for "Retour"
            case 'returnReceived': return 'bg-purple-50 border-purple-200 text-purple-800'; // Style for "Retour reçu"
            case 'dechet': return 'bg-red-100 border-red-300 text-red-900'; // Style for "Déchet"
            default: return 'bg-gray-100 border-gray-200 text-gray-800';
        }
    };

    const handleStatusClick = (statusKey) => {
        // Send the key directly to the filter change handler
        onStatusFilterChange(statusApiMapping[statusKey]);

        // On mobile/tablet, scroll to the first order card after the filter is applied.
        // Use 1024 to match the `lg:hidden` breakpoint used by MobileOrderList.
        if (window.innerWidth < 1024) {
            const scrollToList = (attempt = 0) => {
                const orderList = document.getElementById('mobile-order-list');
                if (orderList) {
                    orderList.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else if (attempt < 8) {
                    setTimeout(() => scrollToList(attempt + 1), 120);
                }
            };
            setTimeout(() => scrollToList(), 150);
        }
    };

    const getActiveStyle = (key) => {
        return activeKey === key
            ? 'ring-2 ring-offset-1 ring-[#174540] scale-[1.03] shadow-md'
            : '';
    };

    return (
        <div ref={containerRef} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('allOrders')} ${getActiveStyle('allOrders')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('allOrders')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaShoppingCart className="mr-2" />
                    {statusSummary.allOrders}
                </div>
                <div className="text-xs uppercase font-medium">Toutes les commandes</div>
            </div>
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('abandoned')} ${getActiveStyle('abandoned')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('abandoned')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaTimesCircle className="mr-2" />
                    {statusSummary.abandoned}
                </div>
                <div className="text-xs uppercase font-medium">Panier abandonné</div>
            </div>
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('pending')} ${getActiveStyle('pending')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('pending')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaClock className="mr-2" />
                    {statusSummary.pending}
                </div>
                <div className="text-xs uppercase font-medium">En attente</div>
            </div>
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('confirmed')} ${getActiveStyle('confirmed')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('confirmed')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaCheckCircle className="mr-2" />
                    {statusSummary.confirmed}
                </div>
                <div className="text-xs uppercase font-medium">Confirmées</div>
            </div>
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('readyToShip')} ${getActiveStyle('readyToShip')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('readyToShip')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaBoxOpen className="mr-2" />
                    {statusSummary.readyToShip}
                </div>
                <div className="text-xs uppercase font-medium">Prêt à expédier</div>
            </div>
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('shipped')} ${getActiveStyle('shipped')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('shipped')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaShippingFast className="mr-2" />
                    {statusSummary.shipped}
                </div>
                <div className="text-xs uppercase font-medium">Expédiées</div>
            </div>
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('delivered')} ${getActiveStyle('delivered')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('delivered')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaTruck className="mr-2" />
                    {statusSummary.delivered}
                </div>
                <div className="text-xs uppercase font-medium">Livrées</div>
            </div>
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('canceled')} ${getActiveStyle('canceled')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('canceled')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaTimesCircle className="mr-2" />
                    {statusSummary.canceled}
                </div>
                <div className="text-xs uppercase font-medium">Annulées</div>
            </div>
            
            {/* New status: Returned (Retour) */}
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('return')} ${getActiveStyle('return')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('return')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaExchangeAlt className="mr-2" />
                    {statusSummary.return || 0}
                </div>
                <div className="text-xs uppercase font-medium">Retour</div>
            </div>
            
            {/* New status: Return Received (Retour reçu) - using FaBox instead of FaBoxCheck */}
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('returnReceived')} ${getActiveStyle('returnReceived')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('returnReceived')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaBox className="mr-2" />
                    {statusSummary.returnReceived || 0}
                </div>
                <div className="text-xs uppercase font-medium">Retour reçu</div>
            </div>
            
            {/* New status: Paid (Payé) - using FaMoneyBill instead of FaMoneyCheckAlt */}
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('paid')} ${getActiveStyle('paid')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('paid')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaMoneyBill className="mr-2" />
                    {statusSummary.paid || 0}
                </div>
                <div className="text-xs uppercase font-medium">Payé</div>
            </div>
            
            {/* New status: Déchet */}
            <div 
                className={`border rounded-lg p-2 text-center ${getStatusBadgeStyle('dechet')} ${getActiveStyle('dechet')} cursor-pointer hover:opacity-80 transition-all duration-200`}
                onClick={() => handleStatusClick('dechet')}
            >
                <div className="font-bold text-xl flex items-center justify-center">
                    <FaTimesCircle className="mr-2" />
                    {statusSummary.dechet || 0}
                </div>
                <div className="text-xs uppercase font-medium">Déchet</div>
            </div>
        </div>
    );
};

export default StatusSummary;